import type { ActorDto } from '@newo/api-client';
import { storage } from '@newo/storage';
import { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { v4 } from 'uuid';
import { create } from 'zustand';
import { persist, type StorageValue } from 'zustand/middleware';
import type { CallCreationFinishedPayload } from '@/types/agent-realtime.ts';
import type { AgentData, CustomerData } from '@/types/common.ts';

type TAgentQuery = {
	title?: string | null;
	subtitle?: string | null;
	buttonLabel?: string | null;
};

enum CreatorChatFlowCallState {
	Idle = 'idle',
	Starting = 'starting',
	Active = 'active'
}

type IAppFields = {
	actor: ActorDto | null;
	businessSource?: string | null;
	clientSecret?: string;
	connectorIdn?: string;
	creatorChatFlowCallState: CreatorChatFlowCallState;
	creatorChatFlowHasOpenedFinalizationModal: boolean;
	creatorChatFlowPendingCallCreationFinished?: CallCreationFinishedPayload;
	externalId?: string | null;
	customerIdn: string | null;
	agentData?: Partial<AgentData>;
	customerData?: Partial<CustomerData>;
	agentSuccessActiveStep: AgentSuccessStep;
	isLocked: boolean;
	ionosManagerEmail: string;
	ionosManagerPhone: string;
	ionosOnboardingFinished: boolean;
	ionosTestCallFinished: boolean;
	ionosTestCallStepHidden: boolean;
	isRedirectingToPortal: boolean;
	setIsRedirectingToPortal: (value: boolean) => void;
	setIonosManagerContact: (payload: {
		managerEmail?: string;
		managerPhone?: string;
	}) => void;
	setIsLocked: (value: boolean) => void;
	setIonosOnboardingFinished: (value: boolean) => void;
	setIonosTestCallFinished: (value: boolean) => void;
	setIonosTestCallStepHidden: (value: boolean) => void;
	setCreatorChatFlowHasOpenedFinalizationModal: (value: boolean) => void;
	setCreatorChatFlowPendingCallCreationFinished: (
		payload?: CallCreationFinishedPayload
	) => void;
	setAgentSuccessStep: (step: AgentSuccessStep) => void;
	setCreatorChatFlowCallState: (state: CreatorChatFlowCallState) => void;
	resetState: () => void;
	setActor: (actor: ActorDto | null) => void;
	setCustomerData: (payload: Partial<CustomerData>) => void;
	setAgentData: (payload: Partial<AgentData>) => void;
	agentQuery?: TAgentQuery;
	setAgentQuery: (payload: TAgentQuery) => void;
	setExternalId: (externalId: string | null) => void;
	setCustomerIdn: (customerIdn: string | null) => void;
	setBusinessSource: (businessSource: string | null) => void;
};

interface IAppState extends IAppFields {
	setAppState: (fields: Partial<IAppFields>) => void;
}

const initialState = {
	clientSecret: undefined,
	connectorIdn: undefined,
	creatorChatFlowCallState: CreatorChatFlowCallState.Idle,
	creatorChatFlowHasOpenedFinalizationModal: false,
	externalId: v4(),
	agentSuccessActiveStep: AgentSuccessStep.ActivateAccount,
	isLocked: false,
	ionosManagerEmail: '',
	ionosManagerPhone: '',
	ionosOnboardingFinished: false,
	ionosTestCallFinished: false,
	ionosTestCallStepHidden: false,
	isRedirectingToPortal: false,
	agentQuery: {
		title: undefined,
		subtitle: undefined,
		buttonLabel: undefined
	},
	actor: null,
	businessSource: null,
	customerIdn: null,
	isSuccessModalAlreadyOpened: false
};

const useAppStore = create<IAppState>()(
	persist(
		(set) => ({
			...initialState,
			resetState: () =>
				set({
					clientSecret: undefined,
					connectorIdn: undefined,
					creatorChatFlowCallState: CreatorChatFlowCallState.Idle,
					creatorChatFlowHasOpenedFinalizationModal: false,
					creatorChatFlowPendingCallCreationFinished: undefined,
					externalId: undefined,
					agentData: undefined,
					customerData: undefined,
					agentSuccessActiveStep: undefined,
					isLocked: false,
					ionosManagerEmail: '',
					ionosManagerPhone: '',
					ionosOnboardingFinished: false,
					ionosTestCallFinished: false,
					ionosTestCallStepHidden: false,
					actor: null,
					customerIdn: null,
					businessSource: null
				}),
			setIsRedirectingToPortal: (isRedirectingToPortal) => {
				set({ isRedirectingToPortal });
			},
			setIonosManagerContact: ({ managerEmail, managerPhone }) => {
				set({
					...(managerEmail != null ? { ionosManagerEmail: managerEmail } : {}),
					...(managerPhone != null ? { ionosManagerPhone: managerPhone } : {})
				});
			},
			setIsLocked: (isLocked) => {
				set({ isLocked });
			},
			setIonosOnboardingFinished: (ionosOnboardingFinished) => {
				set({ ionosOnboardingFinished });
			},
			setIonosTestCallFinished: (ionosTestCallFinished) => {
				set({ ionosTestCallFinished });
			},
			setIonosTestCallStepHidden: (ionosTestCallStepHidden) => {
				set({ ionosTestCallStepHidden });
			},
			setActor: (actor) => {
				set({ actor });
			},
			setCustomerIdn: (customerIdn) => {
				set({ customerIdn });
			},
			setExternalId: (externalId) => {
				set({ externalId });
			},
			setAgentSuccessStep: (step: AgentSuccessStep) =>
				set({ agentSuccessActiveStep: step }),
			setCreatorChatFlowCallState: (creatorChatFlowCallState) =>
				set({ creatorChatFlowCallState }),
			setCreatorChatFlowHasOpenedFinalizationModal: (
				creatorChatFlowHasOpenedFinalizationModal
			) => set({ creatorChatFlowHasOpenedFinalizationModal }),
			setCreatorChatFlowPendingCallCreationFinished: (
				creatorChatFlowPendingCallCreationFinished
			) => set({ creatorChatFlowPendingCallCreationFinished }),
			setAppState: (fields: Partial<IAppFields>) => set(fields),

			setAgentData: (agentData) => {
				set((state) => ({
					agentData: {
						...(state.agentData ?? {}),
						...agentData
					}
				}));
			},

			setCustomerData: (customerData) => {
				set((state) => ({
					customerData: {
						...(state.customerData ?? {}),
						...customerData
					}
				}));
			},
			setBusinessSource: (businessSource) => {
				set({ businessSource });
			},
			setAgentQuery: (payload: Partial<TAgentQuery>) =>
				set((state) => ({
					agentQuery: {
						...state.agentQuery,
						...(payload.title != null ? { title: payload.title } : {}),
						...(payload.subtitle != null ? { subtitle: payload.subtitle } : {}),
						...(payload.buttonLabel != null
							? { buttonLabel: payload.buttonLabel }
							: {})
					}
				}))
		}),
		{
			name: 'app-store',
			storage: {
				getItem: (key) =>
					storage.get<
						StorageValue<
							Omit<
								IAppState,
								| 'agentQuery'
								| 'creatorChatFlowCallState'
								| 'isRedirectingToPortal'
							>
						>
					>(key),
				setItem: (key, value) => storage.set(key, value),
				removeItem: (key) => storage.remove(key)
			},
			partialize: ({
				agentQuery,
				creatorChatFlowCallState,
				isRedirectingToPortal,
				...state
			}) => {
				return state;
			},
			version: 1
		}
	)
);

export default useAppStore;
export { CreatorChatFlowCallState };
